import React from 'react';

/**
 * ResponsiveImage - A generic component that displays images with responsive sizing
 * 
 * @param {Object} props
 * @param {string} props.src - Source image path (relative to public directory)
 * @param {string} props.alt - Alt text for the image
 * @param {string} props.className - CSS class name
 * @param {Object} props.style - Inline styles
 * @param {Function} props.onClick - Click handler
 * @param {Object} props.breakpoints - Custom breakpoints (optional)
 * @param {string} props.cacheDir - Cache directory path (default: 'img/cache')
 * @param {boolean} props.lazy - Enable lazy loading (default: true)
 * @param {React.Ref} ref - Ref to pass to the img element
 */
const ResponsiveImage = React.forwardRef(({ 
  src, 
  alt, 
  className = '', 
  style = {}, 
  onClick,
  breakpoints = {
    small: { maxWidth: 640, width: 1024 },
    medium: { maxWidth: 1024, width: 1280 },
    large: { maxWidth: Infinity, width: 1440 }
  },
  cacheDir = 'img/cache',
  lazy = true
}, ref) => {
  // Extract filename and extension from src
  const pathParts = src.split('/');
  const fileName = pathParts[pathParts.length - 1];
  const fileExt = fileName.split('.').pop();
  const baseName = fileName.replace(`.${fileExt}`, '');
  
  // Generate cache path based on source path
  const srcPath = src.startsWith('/') ? src.slice(1) : src;
  const cachePath = srcPath.replace('img/', `${cacheDir}/`);
  const cacheDirPath = cachePath.substring(0, cachePath.lastIndexOf('/'));
  
  // Generate responsive image sources
  const generateSrcSet = (size) => {
    const sizeConfig = breakpoints[size];
    return `/${cacheDirPath}/${baseName}-${size}-${sizeConfig.width}w.webp ${sizeConfig.width}w`;
  };

  // Fallback to original image if optimized versions don't exist
  const fallbackSrc = src.startsWith('/') ? src : `/${src}`;

  return (
    <picture className={className} style={style}>
      {/* WebP sources for different sizes */}
      <source
        type="image/webp"
        srcSet={generateSrcSet('small')}
        sizes={`${breakpoints.small.width}px`}
        media="(max-width: 640px)"
      />
      <source
        type="image/webp"
        srcSet={generateSrcSet('medium')}
        sizes={`${breakpoints.medium.width}px`}
        media="(max-width: 1024px)"
      />
      <source
        type="image/webp"
        srcSet={generateSrcSet('large')}
        sizes={`${breakpoints.large.width}px`}
      />
      
      {/* Fallback image */}
      <img
        ref={ref}
        src={fallbackSrc}
        alt={alt}
        onClick={onClick}
        loading={lazy ? "lazy" : "eager"}
        decoding="async"
      />
    </picture>
  );
});

export default ResponsiveImage;
